import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Hammer } from 'lucide-react';
import { IMG, SPEAKERS } from '../data';
import { EASE, Reveal, SectionHead, useSite } from './shared';

const API = process.env.REACT_APP_BACKEND_URL || '';

const KIND_STYLE = {
  keynote: 'bg-lime text-ink',
  panel: 'bg-ice/15 text-ice border border-ice/40',
  fireside: 'bg-ice/15 text-ice border border-ice/40',
  roundtable: 'bg-ice/15 text-ice border border-ice/40',
  workshop: 'bg-lime/10 text-lime border border-lime/40',
  presentation: 'bg-line/10 text-slate-200 border border-line/20',
  awards: 'bg-lime/15 text-lime border border-lime/50',
  session: 'bg-line/10 text-slate-200 border border-line/20',
  networking: 'bg-transparent text-slate-400 border border-line/15',
  break: 'bg-transparent text-slate-500 border border-line/10',
};

const CITY_TABS = [
  ['all', 'All Speakers'],
  ['D', 'New Delhi'],
  ['M', 'Mumbai'],
];
const CITY_LABEL = { D: 'New Delhi', M: 'Mumbai' };
const INITIAL_COUNT = 15;

const initials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

function SpeakerCard({ name, role, org, imgSrc, cities, testid }) {
  return (
    <article
      data-testid={testid}
      className="group h-full overflow-hidden rounded-xl border border-line/10 bg-ink-2/60 transition-all duration-500 hover:-translate-y-1 hover:border-lime/50 hover:shadow-[0_10px_40px_rgba(153,204,0,0.12)]"
    >
      <div className="relative aspect-square overflow-hidden">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={name}
            loading="lazy"
            className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-ink-3/70 transition-all duration-700 group-hover:bg-lime/10">
            <span className="font-display text-5xl font-extrabold tracking-tight text-lime/80">{initials(name)}</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink-2/90 via-transparent to-transparent" />
        {cities && cities.length > 0 && (
          <div className="absolute right-2 top-2 flex gap-1">
            {cities.map((c) => (
              <span
                key={c}
                className="rounded-full bg-ink/75 px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.18em] text-lime backdrop-blur-sm"
              >
                {CITY_LABEL[c]}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-display text-[13px] font-bold leading-tight tracking-tight group-hover:text-lime">{name}</h3>
        <p className="mt-2 font-mono text-[9.5px] uppercase leading-relaxed tracking-[0.14em] text-slate-400">{role}</p>
        {org && (
          <p className="mt-1 font-mono text-[9.5px] uppercase leading-relaxed tracking-[0.14em] text-lime/80">{org}</p>
        )}
      </div>
    </article>
  );
}

function AgendaTimeline({ items }) {
  const cities = useMemo(() => {
    const s = new Set();
    items.forEach((a) => {
      if (a.city && a.city !== 'Both') s.add(a.city);
    });
    return [...s];
  }, [items]);
  const [city, setCity] = useState('');
  const active = city || cities[0] || '';
  const list = cities.length ? items.filter((a) => !a.city || a.city === 'Both' || a.city === active) : items;

  return (
    <>
      {cities.length > 1 && (
        <div className="mb-10 flex flex-wrap gap-2">
          {cities.map((c) => (
            <button
              key={c}
              data-testid={`agenda-city-${c.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setCity(c)}
              className={`rounded-full px-5 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-all ${
                active === c ? 'bg-lime text-ink' : 'border border-line/15 text-slate-300 hover:border-lime/50 hover:text-lime'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}
      <div className="relative border-l border-line/10 pl-6 md:pl-10">
        {list.map((a, i) => {
          const speakers = Array.isArray(a.speakers)
            ? a.speakers
            : String(a.speakers || '')
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean);
          return (
            <Reveal key={a._key || i} delay={Math.min(i * 0.03, 0.3)} y={20}>
              <div data-testid={`agenda-item-${i}`} className="relative pb-10">
                <span className="absolute -left-[27px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-ink bg-lime md:-left-[43px]" />
                <p className="font-mono text-[11px] tracking-[0.2em] text-lime">{a.time_slot}</p>
                <h3 className="mt-2 font-display text-base font-bold leading-snug tracking-tight sm:text-lg">{a.title}</h3>
                {a.kind && (
                  <span
                    className={`mt-3 inline-block rounded-full px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] ${
                      KIND_STYLE[a.kind] || KIND_STYLE.session
                    }`}
                  >
                    {a.kind}
                  </span>
                )}
                {a.moderator && (
                  <p className="mt-3 font-mono text-[10.5px] uppercase leading-relaxed tracking-[0.12em] text-ice">
                    Moderator — {a.moderator}
                  </p>
                )}
                {speakers.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {speakers.map((sp, j) => (
                      <span
                        key={j}
                        className="rounded-md border border-line/10 bg-ink-3/70 px-3 py-1.5 text-[11px] leading-snug text-slate-300"
                      >
                        {sp}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          );
        })}
      </div>
    </>
  );
}

function AgendaWip() {
  return (
    <Reveal>
      <div
        data-testid="agenda-wip"
        className="relative overflow-hidden rounded-2xl border border-lime/25 bg-gradient-to-br from-ink-2 via-ink-2/80 to-lime/[0.06] px-6 py-14 text-center md:px-12"
      >
        <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-lime/10 blur-[90px]" />
        <motion.span
          animate={{ rotate: [0, -14, 0, 14, 0] }}
          transition={{ repeat: Infinity, duration: 3.2, ease: 'easeInOut' }}
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-lime/40 bg-lime/10"
        >
          <Hammer size={26} className="text-lime" strokeWidth={1.6} />
        </motion.span>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.35em] text-lime">Work in Progress</p>
        <h3 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl">The agenda is being curated</h3>
        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-400">
          Keynotes, power panels, CXO roundtables and AI workshops for New Delhi (Feb 05, 2027) and Mumbai (Feb 19,
          2027) will be announced soon. Stay tuned.
        </p>
      </div>
    </Reveal>
  );
}

export default function Speakers() {
  const { settings, agenda } = useSite();
  const [speakersData, setSpeakersData] = useState(null);
  const [city, setCity] = useState('all');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/api/speakers`)
      .then((res) => setSpeakersData(Array.isArray(res.data) ? res.data : []))
      .catch(() => setSpeakersData([]));
  }, []);

  const all = useMemo(() => {
    // Database is the single source of truth when loaded
    const list = speakersData !== null ? speakersData : SPEAKERS;
    return list.map((s) => {
      let cities = [];
      if (Array.isArray(s.cities)) {
        cities = s.cities;
      } else if (s.city) {
        cities = s.city.split(',').map((c) => c.trim()).filter(Boolean);
      }
      let imgSrc = null;
      if (s.img) {
        imgSrc = IMG(s.img);
      } else if (s.photo_path) {
        if (s.photo_path.startsWith('/img/') || s.photo_path.startsWith('img/') || s.photo_path.startsWith('http')) {
          imgSrc = s.photo_path;
        } else {
          imgSrc = `${API}/api/files/${s.photo_path}`;
        }
      }
      return {
        key: s._key || s.name,
        name: s.name,
        role: s.role,
        org: s.org || '',
        imgSrc,
        cities,
      };
    });
  }, [speakersData]);
  const filtered = city === 'all' ? all : all.filter((s) => !s.cities.length || s.cities.includes(city));
  const shown = expanded ? filtered : filtered.slice(0, INITIAL_COUNT);
  const showAgenda = String(settings.agenda_visible) === '1' && agenda.length > 0;

  return (
    <>
      <section id="speakers" className="tex-diag border-t border-line/10 bg-ink-2/40 py-24 md:py-32">
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <SectionHead
            eyebrow="Guest & Speakers"
            title={
              <>
                Eminent Guest &amp; <span className="text-lime">Speakers (Last Edition)</span>
              </>

            }
          />
          <Reveal>
            <div className="mb-10 flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter speakers by city">
              {CITY_TABS.map(([id, label]) => (
                <button
                  key={id}
                  role="tab"
                  aria-selected={city === id}
                  data-testid={`speaker-filter-${id}`}
                  onClick={() => {
                    setCity(id);
                    setExpanded(false);
                  }}
                  className={`rounded-full px-5 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-all duration-300 ${
                    city === id
                      ? 'bg-lime text-ink shadow-[0_0_24px_rgba(153,204,0,0.35)]'
                      : 'border border-line/15 text-slate-300 hover:border-lime/50 hover:text-lime'
                  }`}
                >
                  {label}
                </button>
              ))}
              <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                {filtered.length} speakers
              </span>
            </div>
          </Reveal>
          <motion.div layout className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            <AnimatePresence mode="popLayout">
              {shown.map((s, i) => (
                <motion.div
                  key={s.key}
                  layout
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5, delay: Math.min((i % 15) * 0.03, 0.35), ease: EASE }}
                  className="h-full"
                >
                  <SpeakerCard {...s} testid={`speaker-card-${i}`} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
          {filtered.length === 0 && (
            <div className="rounded-2xl border border-line/10 bg-ink-2/40 py-16 text-center">
              <p className="font-mono text-xs uppercase tracking-widest text-slate-500">
                No speakers found for this selection.
              </p>
            </div>
          )}
          {filtered.length > INITIAL_COUNT && (
            <div className="mt-12 flex justify-center">
              <button
                data-testid="speakers-toggle"
                onClick={() => setExpanded((v) => !v)}
                className="group inline-flex items-center gap-3 rounded-full border border-lime/50 px-8 py-3.5 font-display text-xs font-bold uppercase tracking-widest text-lime transition-all duration-300 hover:bg-lime/10 hover:shadow-[0_0_28px_rgba(153,204,0,0.25)]"
              >
                {expanded ? 'Show fewer' : `View all ${filtered.length} speakers`}
                <ChevronDown size={16} className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
              </button>
            </div>
          )}
        </div>
      </section>

      <section id="agenda" className="tex-lines py-24 md:py-32">
        <div className="mx-auto w-full max-w-5xl px-5 md:px-8">
          <SectionHead
            eyebrow="Schedule"
            title={
              <>
                Schedule &amp; <span className="text-lime">Agenda</span>
              </>
            }
          />
          {showAgenda ? <AgendaTimeline items={agenda} /> : <AgendaWip />}
        </div>
      </section>
    </>
  );
}
